import type { AnyClock } from "@repo/lib/clock";
import { ObjectKeyHasher } from "@repo/lib/keyHasher";
import {
  RepoCache,
  type GetObservableOptions,
} from "@repo/shared-client/repoCache";
import {
  CreateLetterHeadDtoSchema,
  PatchLetterHeadDtoSchema,
  type LetterHead,
} from "@repo/types";
import { cacheDefaults } from "./common";
import { apiService } from "@/services/apiService";
import { of, type Observable } from "rxjs";
import { nullObservable } from "@repo/shared-client/commonObservables";
import type { OnlyRequire } from "@repo/lib/types";
import { RepoMutationHooks } from "./repoMutationHooks";
import { baseRepo } from "./baseRepo";

type LetterHeadGetKey = { type: "get"; tenantId: string; letterHeadId: string };
type LetterHeadListKey = { type: "list"; tenantId: string };
type LetterHeadKey = LetterHeadGetKey | LetterHeadListKey;

export type LetterHeadDto = LetterHead & {
  tenantId: string;
};

export type LetterHeadIdPath = {
  tenantId: string;
  letterHeadId: string | null;
};

export class LetterHeadRepository {
  private cache: RepoCache<string, LetterHeadDto, ObjectKeyHasher, AnyClock>;
  private hooks = new RepoMutationHooks<LetterHeadIdPath, LetterHeadDto>();

  // expose hook registry
  addHook = this.hooks.addHook;
  removeHook = this.hooks.removeHook;

  publishNewer(letterHead: LetterHeadDto) {
    this.cache.publish(letterHead.id, letterHead);
  }

  letterHeadToDto(tenantId: string, letterHead: LetterHead): LetterHeadDto;
  letterHeadToDto(tenantId: string, letterHead?: null): null;
  letterHeadToDto(
    tenantId: string,
    letterHead?: LetterHead | null
  ): LetterHeadDto | null;
  letterHeadToDto(
    tenantId: string,
    letterHead?: LetterHead | null
  ): LetterHeadDto | null {
    return letterHead ? { ...letterHead, tenantId } : null;
  }

  constructor() {
    this.cache = new RepoCache({
      requestKeyHasher: new ObjectKeyHasher({ sortKeys: false }),
      expiresAfterSeconds: cacheDefaults.expiresAfterSeconds,
      purgeExpirationAfterSeconds: cacheDefaults.purgeExpiredAfterSeconds,
      fetch: async ({ requestKey, publish }) => {
        const key = requestKey as LetterHeadKey;

        switch (key.type) {
          case "get": {
            const { tenantId, letterHeadId } = key;
            const letterHead = (
              await apiService.v1.getLetterhead(tenantId, letterHeadId)
            ).data;

            // only publish if it's the right tenant
            const prev = this.cache.table.get(letterHeadId);
            if (!prev || prev.tenantId === tenantId) {
              publish(letterHeadId, this.letterHeadToDto(tenantId, letterHead));
            }
            break;
          }
          case "list": {
            const { tenantId } = key;
            const letterHeads = (await apiService.v1.listLetterheads(tenantId))
              .data;
            for (const letterHead of letterHeads) {
              publish(
                letterHead.id,
                this.letterHeadToDto(tenantId, letterHead)
              );
            }
            break;
          }
        }
      },
      collect: ({ requestKey, table }) => {
        const key = requestKey as LetterHeadKey;

        switch (key.type) {
          case "get": {
            return table.get(key.letterHeadId) ?? null;
          }
          case "list": {
            const letterHeads: LetterHeadDto[] = [];
            for (const letterHead of table.values()) {
              if (letterHead.tenantId === key.tenantId) {
                letterHeads.push(letterHead);
              }
            }
            return letterHeads;
          }
        }
      },
      affectedKeys: ({ entryData, affectsKey }) => {
        // extract
        const { tenantId, id } = entryData;
        // affects
        affectsKey(this.getKey(tenantId, id));
        affectsKey(this.listKey(tenantId));
      },
    });

    baseRepo.addRepo(this);
  }

  private getKey(tenantId: string, letterHeadId: string): LetterHeadGetKey {
    return { type: "get", tenantId, letterHeadId };
  }
  private listKey(tenantId: string): LetterHeadListKey {
    return { type: "list", tenantId };
  }

  get(
    tenantId: string,
    letterHeadId: string,
    options?: GetObservableOptions
  ): Observable<LetterHeadDto | null> {
    if (!tenantId || !letterHeadId) {
      return nullObservable;
    }
    const key = this.getKey(tenantId, letterHeadId);
    return this.cache.observable(key, options);
  }
  getCached(tenantId: string, letterHeadId: string): LetterHeadDto | null {
    const key = this.getKey(tenantId, letterHeadId);
    return this.cache.collectIfUnexpired(key, null);
  }

  list(
    tenantId: string,
    options?: GetObservableOptions
  ): Observable<LetterHeadDto[]> {
    if (!tenantId) {
      return of([]);
    }
    const key = this.listKey(tenantId);
    return this.cache.observable(key, options);
  }
  listCached(tenantId: string): LetterHeadDto[] | null {
    const key = this.listKey(tenantId);
    return this.cache.collectIfUnexpired(key, null);
  }

  async create(
    tenantId: string,
    data: Partial<LetterHeadDto>
  ): Promise<LetterHeadDto> {
    if (!tenantId) {
      throw new Error("Failed to create letterhead: tenantId is blank.");
    }

    const hooks = this.hooks.execute(
      { tenantId, letterHeadId: null },
      "create"
    );
    data = await hooks.before(data);

    let created = this.letterHeadToDto(
      tenantId,
      (
        await apiService.v1.createLetterhead(
          tenantId,
          CreateLetterHeadDtoSchema.parse(data)
        )
      ).data
    );

    created = await hooks.after(created);

    // publish new letterhead
    this.cache.publish(created.id, created);

    return created;
  }

  async update(
    tenantId: string,
    letterHeadId: string,
    data: Partial<LetterHeadDto>
  ): Promise<LetterHeadDto> {
    if (!tenantId || !letterHeadId) {
      throw new Error(
        "Failed to update letterhead: tenantId or letterHeadId is blank."
      );
    }

    const hooks = this.hooks.execute({ tenantId, letterHeadId }, "update");
    data = await hooks.before(data);

    let updated = this.letterHeadToDto(
      tenantId,
      (
        await apiService.v1.updateLetterhead(
          tenantId,
          letterHeadId,
          PatchLetterHeadDtoSchema.parse(data)
        )
      ).data
    );

    updated = await hooks.after(updated);

    // publish updated letterhead
    this.cache.publish(updated.id, updated);

    return updated;
  }

  async delete(tenantId: string, letterHeadId: string): Promise<void> {
    if (!tenantId || !letterHeadId) {
      throw new Error(
        "Failed to delete letterhead: tenantId or letterHeadId is blank."
      );
    }

    const hooks = this.hooks.execute({ tenantId, letterHeadId }, "delete");
    await hooks.before(null);

    await apiService.v1.deleteLetterhead(tenantId, letterHeadId);

    await hooks.after(null);
    this.cache.publish(letterHeadId, null);
  }

  invalidate(letterHead: OnlyRequire<LetterHeadDto, "id">) {
    this.cache.invalidateEntry(letterHead.id);
  }

  invalidateAll() {
    this.cache.clear();
  }

  init(data?: Partial<LetterHeadDto>): LetterHeadDto {
    return baseRepo.init({
      tenantId: baseRepo.getTenantId() || "UNKNOWN",
      status: "draft",
      members: [],
      name: "",
      viewName: "",
      email: "",
      phoneNumber: "",
      logoUrl: "",
      ...data,
    });
  }
}

export const letterHeadRepo = new LetterHeadRepository();
